import { useSessionStore } from "../stores/sessionStore";
import { useAuthStore }    from "../stores/authStore";

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS  = 30_000;
const PING_INTERVAL_MS  = 25_000;

let ws               = null;
let reconnectTimer   = null;
let pingTimer        = null;
let reconnectDelay   = RECONNECT_BASE_MS;
let intentionalClose = false;

function _wsUrl() {
  const authStore = useAuthStore();

  if (!authStore.apiBase || !authStore.apiKey) return null;

  const base = authStore.apiBase
    .replace(/^https/, "wss")
    .replace(/^http/,  "ws");

  return `${base}/ws?token=${authStore.apiKey}`;
}

function _startPing() {
  _stopPing();
  pingTimer = setInterval(() => {
    if (ws?.readyState === WebSocket.OPEN) {
      try { ws.send("ping"); } catch {}
    }
  }, PING_INTERVAL_MS);
}

function _stopPing() {
  if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
}

function _scheduleReconnect() {
  if (intentionalClose) return;
  reconnectTimer = setTimeout(() => {
    _connect();
    reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
  }, reconnectDelay);
}

function _handleEvent(raw) {
  let event;
  try { event = JSON.parse(raw); } catch { return; }

  const { event: type, session_id } = event;

  switch (type) {
    case "session.uploaded":
      _addSessionStub(session_id, event.filename);
      break;
    case "session.stage":
      _patchSessionStage(session_id, event);
      _dispatchDom("pipeline-stage", event);
      break;
    case "session.completed":
      _patchSessionStatus(session_id, "completed", event.risk);
      _reloadSessions(); // reload to get full final data (verdict, duration, etc.)
      _dispatchDom("session-completed", event);
      break;
    case "session.failed":
      _patchSessionStatus(session_id, "failed");
      _reloadSessions();
      _dispatchDom("session-failed", event);
      break;
    case "detection.created":
      _dispatchDom("detection-created", event);
      break;
  }
}

function _reloadSessions() {
  try { useSessionStore().loadSessions(); } catch {}
}

function _addSessionStub(sessionId, filename) {
  try {
    const store = useSessionStore();
    // Don't add if already present
    if (store.sessions.find((s) => s.id === sessionId)) return;
    store.sessions.unshift({
      id:         sessionId,
      filename:   filename ?? sessionId,
      status:     "uploaded",
      file_size:  null,
      protocol:   null,
      guac:       null,
      hls:        null,
      ocr:        null,
      vlm:        null,
      llm:        null,
      created_at: Date.now() / 1000,
    });
  } catch {}
}

function _patchSessionStatus(sessionId, status, risk) {
  try {
    const store = useSessionStore();
    const idx   = store.sessions.findIndex((s) => s.id === sessionId);
    if (idx === -1) { _reloadSessions(); return; }
    const session = store.sessions[idx];
    store.sessions.splice(idx, 1, {
      ...session,
      status,
      ...(risk !== undefined && { risk }),
    });
  } catch {
    _reloadSessions();
  }
}

function _patchSessionStage(sessionId, event) {
  try {
    const store = useSessionStore();
    const idx   = store.sessions.findIndex((s) => s.id === sessionId);
    if (idx === -1) return;
    const session = store.sessions[idx];

    // Build updated stage — merge existing data with new fields from event
    const existing = session[event.stage] ?? {};
    const updated  = {
      ...existing,
      status: event.status,
      ...(event.frames       !== undefined && { frames:       event.frames }),
      ...(event.duration_ms  !== undefined && { duration_ms:  event.duration_ms }),
      ...(event.segments     !== undefined && { segments:     event.segments }),
      ...(event.commands     !== undefined && { commands:     event.commands }),
      ...(event.processing_ms !== undefined && { processing_ms: event.processing_ms }),
    };

    // splice creates a new object reference → Vue detects the change reactively
    store.sessions.splice(idx, 1, { ...session, [event.stage]: updated });
  } catch (e) {
    console.error("[wsManager] _patchSessionStage error:", e);
  }
}

function _dispatchDom(name, detail) {
  window.dispatchEvent(new CustomEvent(`ws:${name}`, { detail }));
}

function _connect() {
  if (ws && ws.readyState !== WebSocket.CLOSED) return;

  const url = _wsUrl();
  if (!url) {
    console.log("[wsManager] No backend configured — skipping WebSocket connection");
    return;
  }

  console.log("[wsManager] Connecting to", url);
  try { ws = new WebSocket(url); } catch (err) {
    console.error("[wsManager] WebSocket constructor failed:", err);
    _scheduleReconnect();
    return;
  }

  ws.onopen    = () => {
    console.log("[wsManager] Connected");
    reconnectDelay = RECONNECT_BASE_MS;
    _startPing();
    _reloadSessions(); // sync state after (re)connect — events may have been missed
    window.dispatchEvent(new CustomEvent("ws:backend-connected"));
  };
  ws.onmessage = (e) => _handleEvent(e.data);
  ws.onerror   = (e) => console.warn("[wsManager] Error:", e);
  ws.onclose   = (e) => {
    _stopPing();
    console.log(`[wsManager] Disconnected (${e.code})`);
    window.dispatchEvent(new CustomEvent("ws:backend-disconnected"));
    _scheduleReconnect();
  };
}

export function connect() {
  intentionalClose = false;
  _connect();
}

export function disconnect() {
  intentionalClose = true;
  _stopPing();
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  if (ws) { try { ws.close(1000, "Client disconnect"); } catch {} ws = null; }
  console.log("[wsManager] Disconnected intentionally");
}

export function isConnected() {
  return ws?.readyState === WebSocket.OPEN;
}

export const wsManager = { connect, disconnect, isConnected };